import * as React from 'react';
import { Link } from 'react-router-dom';
import './style.scss';
import { TableComponent } from '../../components/table';
import { getFormattedDate } from '../../utils/utils';

interface ClosedPosition {
    symbol: string;
    quantity: number;
    avgBuyPrice: number;
    avgSellPrice: number;
    totalBuyValue: number;
    totalSellValue: number;
    profitLoss: number;
    profitLossPercent: number;
    trades: number;
    closedDate: string | null;
}

interface ClosedPositionsPageProps {
    closedPositions: ClosedPosition[];
    chartPeriod: string;
    updatePeriod: (period: string) => void;
}

export const ClosedPositionsPage: React.FC<ClosedPositionsPageProps> = ({ closedPositions, chartPeriod, updatePeriod }) => {
    const columns = [
        {
            key: 'symbol',
            label: 'Symbol',
            renderer: (row: ClosedPosition) => (
                <Link to={`/symbol/${row.symbol}`}>
                    <b>{row.symbol}</b>
                </Link>
            )
        },
        {
            key: 'quantity',
            label: 'Qty',
            renderer: (row: ClosedPosition) => parseFloat(row.quantity.toString()).toFixed(2)
        },
        {
            key: 'avgBuyPrice',
            label: 'Avg Buy Price',
            renderer: (row: ClosedPosition) => `$${row.avgBuyPrice.toFixed(2)}`
        },
        {
            key: 'avgSellPrice',
            label: 'Avg Sell Price',
            renderer: (row: ClosedPosition) => `$${row.avgSellPrice.toFixed(2)}`
        },
        {
            key: 'totalBuyValue',
            label: 'Total Cost',
            renderer: (row: ClosedPosition) => `$${row.totalBuyValue.toFixed(2)}`
        },
        {
            key: 'totalSellValue',
            label: 'Total Revenue',
            renderer: (row: ClosedPosition) => `$${row.totalSellValue.toFixed(2)}`
        },
        {
            key: 'profitLoss',
            label: 'Profit/Loss',
            renderer: (row: ClosedPosition) => (
                <div style={{
                    color: row.profitLoss >= 0 ? 'rgb(0, 200, 20)' : 'rgb(255, 80, 0)',
                    fontWeight: 600
                }}>
                    {row.profitLoss >= 0 ? '✓' : '✗'} ${Math.abs(row.profitLoss).toFixed(2)}
                </div>
            )
        },
        {
            key: 'profitLossPercent',
            label: 'Return %',
            renderer: (row: ClosedPosition) => (
                <div style={{
                    color: row.profitLossPercent >= 0 ? 'rgb(0, 200, 20)' : 'rgb(255, 80, 0)',
                    fontWeight: 600
                }}>
                    {row.profitLossPercent >= 0 ? '↑' : '↓'} {row.profitLossPercent.toFixed(2)}%
                </div>
            )
        },
        {
            key: 'trades',
            label: 'Trades',
            renderer: (row: ClosedPosition) => row.trades
        },
        {
            key: 'closedDate',
            label: 'Closed Date',
            renderer: (row: ClosedPosition) => row.closedDate ? getFormattedDate(new Date(row.closedDate)) : '-'
        }
    ];

    return (
        <div className='closed-positions-page'>
            {/* Header Section */}
            <div className='page-header'>
                <div>
                    <h1>Closed Positions</h1>
                    <p>View all closed positions ordered by profitability</p>
                </div>
                <div className='period-selector'>
                    <button className={chartPeriod === '1D' ? 'active' : ''} onClick={() => updatePeriod('1D')}>1D</button>
                    <button className={chartPeriod === '1W' ? 'active' : ''} onClick={() => updatePeriod('1W')}>1W</button>
                    <button className={chartPeriod === '1M' ? 'active' : ''} onClick={() => updatePeriod('1M')}>1M</button>
                    <button className={chartPeriod === '3M' ? 'active' : ''} onClick={() => updatePeriod('3M')}>3M</button>
                    <button className={chartPeriod === 'YTD' ? 'active' : ''} onClick={() => updatePeriod('YTD')}>YTD</button>
                    <button className={chartPeriod === '1Y' ? 'active' : ''} onClick={() => updatePeriod('1Y')}>1Y</button>
                    <button className={chartPeriod === 'all' ? 'active' : ''} onClick={() => updatePeriod('all')}>ALL</button>
                </div>
            </div>

            {/* Summary Stats */}
            <div className='summary-stats'>
                <div className='stat-card'>
                    <div className='stat-label'>Total Closed</div>
                    <div className='stat-value'>{closedPositions.length}</div>
                </div>
                <div className='stat-card'>
                    <div className='stat-label'>Total P/L</div>
                    <div className={`stat-value ${closedPositions.reduce((sum, pos) => sum + pos.profitLoss, 0) >= 0 ? 'positive' : 'negative'}`}>
                        ${closedPositions.reduce((sum, pos) => sum + pos.profitLoss, 0).toFixed(2)}
                    </div>
                </div>
                <div className='stat-card'>
                    <div className='stat-label'>Profitable</div>
                    <div className='stat-value success'>
                        {closedPositions.filter(pos => pos.profitLoss > 0).length}
                    </div>
                </div>
                <div className='stat-card'>
                    <div className='stat-label'>Losing</div>
                    <div className='stat-value negative'>
                        {closedPositions.filter(pos => pos.profitLoss < 0).length}
                    </div>
                </div>
            </div>

            {/* Closed Positions Table */}
            <div className='table-section'>
                {closedPositions.length > 0 ? (
                    <TableComponent 
                        data={closedPositions} 
                        columns={columns} 
                        itemsPerPage={50}
                        title={<h2>Position Details</h2>}
                    />
                ) : (
                    <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                        <p>No closed positions yet</p>
                    </div>
                )}
            </div>
        </div>
    );
};
